/**
 * useResponsiveLayout - Responsive layout management hook
 *
 * BYK-168: Responsive collapsing for smaller screens
 *
 * Breakpoints:
 * - Desktop (>1280px): Both sidebars visible
 * - Tablet (768-1280px): Right sidebar auto-collapses
 * - Mobile (<768px): Both sidebars as slide-out drawers
 */

import { useState, useEffect, useCallback } from "react";

// ============================================================================
// Types
// ============================================================================

export type Breakpoint = "mobile" | "tablet" | "desktop";

export interface ResponsiveLayoutState {
  /** Current breakpoint */
  breakpoint: Breakpoint;
  /** Whether we're in mobile mode (drawer navigation) */
  isMobile: boolean;
  /** Whether we're in tablet mode */
  isTablet: boolean;
  /** Whether we're in desktop mode */
  isDesktop: boolean;
  /** Current viewport width */
  width: number;
  /** Whether left drawer is open (mobile only) */
  leftDrawerOpen: boolean;
  /** Whether right drawer is open (mobile only) */
  rightDrawerOpen: boolean;
  /** Open left drawer */
  openLeftDrawer: () => void;
  /** Close left drawer */
  closeLeftDrawer: () => void;
  /** Open right drawer */
  openRightDrawer: () => void;
  /** Close right drawer */
  closeRightDrawer: () => void;
  /** Close all drawers */
  closeAllDrawers: () => void;
}

// ============================================================================
// Constants
// ============================================================================

export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1280,
} as const;

// ============================================================================
// Hook Implementation
// ============================================================================

export function useResponsiveLayout(): ResponsiveLayoutState {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280
  );
  const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);

  // Calculate breakpoint from width
  const getBreakpoint = useCallback((w: number): Breakpoint => {
    if (w < BREAKPOINTS.mobile) return "mobile";
    if (w < BREAKPOINTS.tablet) return "tablet";
    return "desktop";
  }, []);

  const breakpoint = getBreakpoint(width);
  const isMobile = breakpoint === "mobile";
  const isTablet = breakpoint === "tablet";
  const isDesktop = breakpoint === "desktop";

  // Handle resize using ResizeObserver only (more performant than window.resize)
  // Also close drawers when transitioning to desktop (in callback, not sync in effect)
  useEffect(() => {
    let previousWidth = window.innerWidth;

    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWidth(newWidth);

      // Close drawers when transitioning TO desktop from non-desktop
      const wasDesktop = previousWidth >= BREAKPOINTS.tablet;
      const nowDesktop = newWidth >= BREAKPOINTS.tablet;
      if (!wasDesktop && nowDesktop) {
        setLeftDrawerOpen(false);
        setRightDrawerOpen(false);
      }
      previousWidth = newWidth;
    };

    // ResizeObserver is sufficient - window.resize is redundant
    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Drawer controls
  const openLeftDrawer = useCallback(() => {
    setLeftDrawerOpen(true);
    setRightDrawerOpen(false); // Close other drawer
  }, []);

  const closeLeftDrawer = useCallback(() => {
    setLeftDrawerOpen(false);
  }, []);

  const openRightDrawer = useCallback(() => {
    setRightDrawerOpen(true);
    setLeftDrawerOpen(false); // Close other drawer
  }, []);

  const closeRightDrawer = useCallback(() => {
    setRightDrawerOpen(false);
  }, []);

  const closeAllDrawers = useCallback(() => {
    setLeftDrawerOpen(false);
    setRightDrawerOpen(false);
  }, []);

  return {
    breakpoint,
    isMobile,
    isTablet,
    isDesktop,
    width,
    leftDrawerOpen,
    rightDrawerOpen,
    openLeftDrawer,
    closeLeftDrawer,
    openRightDrawer,
    closeRightDrawer,
    closeAllDrawers,
  };
}

export default useResponsiveLayout;
